import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccessAlert } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

interface CreateBuildingModuleProps {
  show: boolean;
  onHide: () => void;
  locationId: number|null ;
  locationName: string;
  onSuccess: () => void; // callback to refresh list in parent
}

const CreateBuildingModule: React.FC<CreateBuildingModuleProps> = ({
  show,
  onHide,
  locationId,
  locationName,
  onSuccess,
}) => {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [noOfFloors, setNoOfFloors] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const resetForm = () => {
    setName('');
    setDescription('');
    setNoOfFloors('');
  };

  const validate = () => {
    if (!name.trim()) {
      showError('Please enter a building name');
      return false;
    }
    if (!noOfFloors || Number(noOfFloors) <= 0) {
      showError('Please enter a valid number of floors');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!checkPermission('ROLE_CREATE_BUILDING')) {
      showError('You do not have permission to create buildings.');
      return;
    }
    if (!validate()) return;

    try {
      setSubmitting(true);

      // Adjust this payload/endpoint name to match your backend
      const payload = {
        locationId,
        name: name.trim(),
        description: description?.trim() || null,
        noOfFloors: Number(noOfFloors),
      };

      // Assumes you have an API like this in apiDefinitions.
      // If your real name differs, update here (e.g., apiDefinitions.createBuildingForLocation).
      const res = await apiDefinitions.addBuilding(payload);

      if (res.status === 200 || res.status === 201) {
        showSuccessAlert("Building created successfully");
        resetForm();
        onSuccess(); // let parent refresh buildings + close modal
      } else {
        showError(res.data?.statusDescription || 'Failed to create building');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Please log in again.');
      } else if (err.response?.status === 409) {
        showError('A building with this name already exists at the selected location.');
      } else {
        showError('Failed to create building. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onHide();
      resetForm();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={!submitting}>
      <div className="modal-header">
        <h5 className="modal-title">Create Building</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} />
      </div>
      <div className="modal-body">
        <div className="mb-3">
          <label className="form-label">Location</label>
          <input className="form-control" value={locationName} disabled />
        </div>

        <div className="mb-3">
          <label className="form-label">Building Name <span className="text-danger">*</span></label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Admin Block"
            disabled={submitting}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Number of Floors <span className="text-danger">*</span></label>
          <input
            type="number"
            className="form-control"
            value={noOfFloors}
            onChange={(e) => setNoOfFloors(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="e.g., 5"
            min={1}
            disabled={submitting}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional notes about this building"
            disabled={submitting}
          />
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-primary" onClick={handleClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn btn-success"
          onClick={handleSave}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Building'}
        </button>
      </div>
    </Modal>
  );
};

export default CreateBuildingModule;
